import { ENV } from '@/configs/env';
import { useSignin } from '@hooks/useSignin';
import { useEdocEntry } from '@hooks/useEdocEntry';
import { EdocVerifiedView } from './edoc/EdocVerifiedView';
import { useMessage } from '@context/MessageProvider';
import { useNotification } from '@context/NotificationProvider';
import { Form, Input, Button, Typography, Spin, Modal } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useThemeStore from '@stores/theme.store';
import dayjs from 'dayjs';
import ScrollToTop from '@utils/scrollToTop';
import { OutsideContainer, OutsideLayoutContent, OutsideContent } from '@layout/style';
import { useEdocSessionStore } from '@stores/edocSession.store';
import { useQueryClient } from '@tanstack/react-query';
import {
    BannerPanel,
    LogoImage,
    BannerImage,
    IconWrapper,
    FormWrapper,
    FormInner,
    BannerHeadline,
    BannerSubheadline
} from '@/pages/style';
import logoDocs from '@assets/logo-docs.png';
import { publicAsset } from '@utils/publicAsset';
import {
    areOriginDestinationAddressesSame,
    ORIGIN_DESTINATION_SAME_ADDRESS_MESSAGE
} from '@utils/addressMatch';

import {
    Container,
    AuthCard,
    MobileLogoImage,
    AgentAssistBar,
    AgentAssistIdentity,
    AgentAssistAvatar,
    AgentAssistName,
    AgentAssistDivider,
    AgentAssistLink,
    AgentAssistMuted,
    IdentityHint
} from '../style';
import logoLight from '@assets/logo-light.png';
import logoDark from '@assets/logo-dark.png';

function maskEmail(email) {
    if (!email || !email.includes('@')) return '';
    const [local, domain] = email.split('@');
    if (!local || !domain) return '';
    const [domainName, ...rest] = domain.split('.');
    const tld = rest.length ? `.${rest.join('.')}` : '';

    const maskPart = (s, keepStart = 1, keepEnd = 1) => {
        if (!s) return '';
        if (s.length <= keepStart + keepEnd) return s;
        return `${s.slice(0, keepStart)}${'*'.repeat(Math.max(1, s.length - keepStart - keepEnd))}${s.slice(-keepEnd)}`;
    };

    return `${maskPart(local, 1, 1)}@${maskPart(domainName, 1, 1)}${tld}`;
}

function maskPhone(phone) {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 4) return phone;
    const last4 = digits.slice(-4);
    const countryPrefix = phone.trim().startsWith('+') ? '+' : '';
    return `${countryPrefix}${'*'.repeat(Math.max(0, digits.length - 4))}${last4}`;
}

function toTelHref(phone) {
    if (!phone) return '#';
    const trimmed = String(phone).trim();
    if (trimmed.startsWith('+')) {
        return `tel:${trimmed.replace(/[^\d+]/g, '')}`;
    }
    const digits = trimmed.replace(/\D/g, '');
    return digits ? `tel:${digits}` : '#';
}

function buildMailtoAgent() {
    if (!ENV.AGENT_EMAIL) return '#';
    const agent = ENV.AGENT_NAME || 'Dispatchly';
    const subject = encodeURIComponent('E-Docs — help with signing');
    const body = encodeURIComponent(
        `Hi ${agent},\n\nI need help confirming my identity / completing my document.\n\nThanks,\n`
    );
    return `mailto:${ENV.AGENT_EMAIL}?subject=${subject}&body=${body}`;
}

const Signin = () => {
    const { form, loading, onFinish } = useSignin();
    const { showMessage } = useMessage();
    const { showNotification } = useNotification();
    const navigate = useNavigate();
    const location = useLocation();
    const { edocNumber } = useParams();
    const queryClient = useQueryClient();
    const mode = useThemeStore((state) => state.mode);
    const isDark = mode === 'dark';
    const [activeStep, setActiveStep] = useState(0);
    const [edocForm] = Form.useForm();
    const [showCompany, setShowCompany] = useState(false);
    const [showAltOriginLine, setShowAltOriginLine] = useState(false);
    const [showAltDestinationLine, setShowAltDestinationLine] = useState(false);
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [signatureName, setSignatureName] = useState('');
    const [ipAddress, setIpAddress] = useState('');
    const [signedModalUrl, setSignedModalUrl] = useState('');
    const {
        enabled: isEdocHome,
        publicEdocQuery,
        publicEdoc,
        cachedPublicEdoc,
        isNotFound: isEdocNotFound,
        isFetchFailed: isEdocFetchFailed,
        isVerified,
        setIsVerified,
        verifyCustomer,
        verifying,
        submitEdoc,
        submitting,
        meQuery
    } = useEdocEntry(edocNumber);

    const agentDisplayName = ENV.AGENT_NAME?.trim() || 'Your agent';
    const hasDocIdentifier = Boolean(String(edocNumber || '').trim());
    const canValidateForEdoc = isEdocHome ? Boolean(publicEdoc) : hasDocIdentifier;

    const snapshot = cachedPublicEdoc?.quoteSnapshot || cachedPublicEdoc?.quote || {};
    const companyInfo = cachedPublicEdoc?.companyInformation || {};
    const payment = cachedPublicEdoc?.payment || null;
    const packages = cachedPublicEdoc?.packages || snapshot?.packages || null;

    const orderContactFallback = useMemo(
        () => ({
            email:
                String(cachedPublicEdoc?.email || '').trim() ||
                String(snapshot?.customerEmail || '').trim(),
            phone:
                String(cachedPublicEdoc?.phone || '').trim() ||
                String(snapshot?.customerPhone || '').trim()
        }),
        [
            cachedPublicEdoc?.email,
            cachedPublicEdoc?.phone,
            snapshot?.customerEmail,
            snapshot?.customerPhone
        ]
    );

    const steps = useMemo(() => ([
        { title: 'Order info' },
        { title: 'Origin' },
        { title: 'Destination' },
        { title: 'Terms & Condition' }
    ]), []);

    const safeText = (v) => {
        const s = String(v ?? '').trim();
        return s ? s : '—';
    };

    // const assignedUserFromEdoc =
    //     publicEdoc?.assignedUser?.name ??
    //     publicEdoc?.quote?.assignedUser ??
    //     publicEdoc?.quoteSnapshot?.assignedUser ??
    //     null;

    const assignedUserLabel = publicEdoc?.assignedUser?.name ? String(publicEdoc.assignedUser.name) : 'N/A';

    const edocEmailHint = publicEdoc?.emailHint ? String(publicEdoc.emailHint) : '';
    const edocPhoneHint = publicEdoc?.phoneHint ? String(publicEdoc.phoneHint) : '';

    const isSessionReady = Boolean(isEdocHome && (isVerified || meQuery?.data));

    useEffect(() => {
        if (!isSessionReady || !cachedPublicEdoc) return;

        const customerName = String(snapshot?.customerName || '').trim();
        const [firstName, ...rest] = customerName.split(' ').filter(Boolean);
        const lastName = rest.join(' ');

        const customerEmail =
            String(cachedPublicEdoc?.email || '').trim() ||
            String(snapshot?.customerEmail || '').trim();
        const customerPhone =
            String(cachedPublicEdoc?.phone || '').trim() ||
            String(snapshot?.customerPhone || '').trim();

        edocForm.setFieldsValue({
            packageDetails: String(
                cachedPublicEdoc?.submission?.packageDetails
                || snapshot?.packageDetails
                || ''
            ).trim(),
            contact: {
                firstName: firstName || '',
                lastName: lastName || '',
                company: String(snapshot?.companyName || '').trim() || '',
                emails: [customerEmail].filter(Boolean),
                phones: [customerPhone].filter(Boolean),
                pickupDate: snapshot?.shippingDate ? dayjs(snapshot.shippingDate) : null
            },
            origin: {
                address1: String(snapshot?.originAddress || '').trim(),
                address2: String(snapshot?.originAddress2 || '').trim(),
                city: String(snapshot?.originCity || '').trim(),
                state: String(snapshot?.originState || '').trim(),
                postalCode: String(snapshot?.originPostalCode || '').trim(),
                isPickupContact: false,
                contactFirstName: '',
                contactLastName: '',
                contactEmail: '',
                contactPhone: ''
            }
            ,
            destination: {
                address1: String(snapshot?.destinationAddress || '').trim(),
                address2: String(snapshot?.destinationAddress2 || '').trim(),
                city: String(snapshot?.destinationCity || '').trim(),
                state: String(snapshot?.destinationState || '').trim(),
                postalCode: String(snapshot?.destinationPostalCode || '').trim(),
                isDestinationContact: false,
                contactFirstName: '',
                contactLastName: '',
                contactEmail: '',
                contactPhone: ''
            }
        });

        if (String(snapshot?.companyName || '').trim()) setShowCompany(true);
        if (String(snapshot?.originAddress2 || '').trim()) setShowAltOriginLine(true);
        if (String(snapshot?.destinationAddress2 || '').trim()) setShowAltDestinationLine(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSessionReady, cachedPublicEdoc]);

    useEffect(() => {
        if (!isSessionReady) return;
        let ignore = false;
        (async () => {
            try {
                const res = await fetch('https://api.ipify.org?format=json');
                const json = await res.json();
                if (!ignore) setIpAddress(String(json?.ip || '').trim());
            } catch {
                if (!ignore) setIpAddress('');
            }
        })();
        return () => {
            ignore = true;
        };
    }, [isSessionReady]);

    const submitButtonText = useMemo(() => {
        if (isEdocHome) return 'Verify & Continue';
        return 'Validate & Continue';
    }, [isEdocHome]);

    const handleSubmit = async (values) => {
        if (!isEdocHome) {
            await onFinish(values);
            return;
        }

        if (!edocNumber) {
            showMessage('error', 'We couldn’t load your document details. Please refresh and try again.');
            return;
        }

        const emailInput = String(values?.email || '').trim();
        const phoneInput = String(values?.phone || '').trim();

        if (!emailInput && !phoneInput) {
            const msg = 'Enter your email or phone number.';
            form.setFields([
                { name: 'email', errors: [msg] },
                { name: 'phone', errors: [msg] }
            ]);
            showMessage('error', msg);
            return;
        }

        if (emailInput) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(emailInput)) {
                form.setFields([{ name: 'email', errors: ['Please enter a valid email address.'] }]);
                showMessage('error', 'Please enter a valid email address.');
                return;
            }
        }
        if (phoneInput) {
            if (!/^[+()\d\s-]{6,40}$/.test(phoneInput)) {
                form.setFields([{ name: 'phone', errors: ['Please enter a valid phone number.'] }]);
                showMessage('error', 'Please enter a valid phone number.');
                return;
            }
        }

        try {
            const result = await verifyCustomer({
                edocNumber: String(edocNumber).trim(),
                edocId: publicEdoc?.id || '',
                email: emailInput || undefined,
                phone: phoneInput || undefined
            });
            if (result?.verified) {
                showNotification(
                    'success',
                    'Verified',
                    'Thanks — your identity has been verified. You can continue to the document.',
                    null,
                    6,
                    'bottomRight'
                );
                return;
            }

            const msg = 'We couldn’t verify your details. Please double-check and try again.';
            showMessage('error', msg, 6);
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'Verification failed. Please try again.';
            showMessage('error', msg, 6);
        }
    };

    const handleBookOrder = async () => {
        try {
            if (!edocNumber) {
                showMessage('error', 'Missing document identifier.', 6);
                return;
            }

            const values = edocForm.getFieldsValue(true);
            const origin = values?.origin || {};
            const destination = values?.destination || {};

            if (areOriginDestinationAddressesSame(origin, destination)) {
                edocForm.setFields([
                    {
                        name: ['destination', 'address1'],
                        errors: [ORIGIN_DESTINATION_SAME_ADDRESS_MESSAGE]
                    }
                ]);
                showMessage('error', ORIGIN_DESTINATION_SAME_ADDRESS_MESSAGE, 6);
                setActiveStep(2);
                return;
            }

            const orderFirstName = String(values?.contact?.firstName || '').trim();
            const orderLastName = String(values?.contact?.lastName || '').trim();
            const orderEmail =
                String((values?.contact?.emails || [])[0] || '').trim() ||
                String(orderContactFallback?.email || '').trim();
            const orderPhone =
                String((values?.contact?.phones || [])[0] || '').trim() ||
                String(orderContactFallback?.phone || '').trim();

            const resolvedDestinationContact = {
                contactFirstName: String(destination?.contactFirstName || '').trim() || (destination?.isDestinationContact ? orderFirstName : ''),
                contactLastName: String(destination?.contactLastName || '').trim() || (destination?.isDestinationContact ? orderLastName : ''),
                contactEmail: String(destination?.contactEmail || '').trim() || (destination?.isDestinationContact ? orderEmail : ''),
                contactPhone: String(destination?.contactPhone || '').trim() || (destination?.isDestinationContact ? orderPhone : '')
            };

            const resolvedOriginContact = {
                contactFirstName: String(origin?.contactFirstName || '').trim() || (origin?.isPickupContact ? orderFirstName : ''),
                contactLastName: String(origin?.contactLastName || '').trim() || (origin?.isPickupContact ? orderLastName : ''),
                contactEmail: String(origin?.contactEmail || '').trim() || (origin?.isPickupContact ? orderEmail : ''),
                contactPhone: String(origin?.contactPhone || '').trim() || (origin?.isPickupContact ? orderPhone : '')
            };

            const originSubmission = {
                originAddress1: String(origin?.address1 || '').trim(),
                originAddress2: String(origin?.address2 || '').trim(),
                originCity: String(origin?.city || '').trim(),
                originState: String(origin?.state || '').trim(),
                originPostalCode: String(origin?.postalCode || '').trim(),
                originContactFirstName: resolvedOriginContact.contactFirstName,
                originContactLastName: resolvedOriginContact.contactLastName,
                originContactEmail: resolvedOriginContact.contactEmail,
                originContactPhone: resolvedOriginContact.contactPhone,
                isPickupContact: Boolean(origin?.isPickupContact)
            };

            const destinationSubmission = {
                address1: String(destination?.address1 || '').trim(),
                address2: String(destination?.address2 || '').trim(),
                city: String(destination?.city || '').trim(),
                state: String(destination?.state || '').trim(),
                postalCode: String(destination?.postalCode || '').trim(),
                contactFirstName: resolvedDestinationContact.contactFirstName,
                contactLastName: resolvedDestinationContact.contactLastName,
                contactEmail: resolvedDestinationContact.contactEmail,
                contactPhone: resolvedDestinationContact.contactPhone,
                isDestinationContact: Boolean(destination?.isDestinationContact),

                // Compatibility: some backends expect destination-prefixed keys
                destinationAddress1: String(destination?.address1 || '').trim(),
                destinationAddress2: String(destination?.address2 || '').trim(),
                destinationCity: String(destination?.city || '').trim(),
                destinationState: String(destination?.state || '').trim(),
                destinationPostalCode: String(destination?.postalCode || '').trim(),
                destinationContactFirstName: resolvedDestinationContact.contactFirstName,
                destinationContactLastName: resolvedDestinationContact.contactLastName,
                destinationContactEmail: resolvedDestinationContact.contactEmail,
                destinationContactPhone: resolvedDestinationContact.contactPhone
            };

            const packageDetails = String(values?.packageDetails || '').trim();

            const payload = {
                submission: {
                    signedBy: String(signatureName || '').trim(),
                    signedAt: new Date().toISOString(),
                    ipAddress: String(ipAddress || '').trim(),
                    acknowledged: Boolean(agreeTerms),
                    ...(packageDetails ? { packageDetails } : {}),
                    ...destinationSubmission,
                    ...originSubmission
                }
            };

            const result = await submitEdoc({ edocNumber, payload });

            const isSigned = Boolean(result?.isSigned);
            const signedUrl =
                String(
                    result?.signedEdocUrl ||
                    result?.edoc?.signedEdocUrl ||
                    result?.data?.signedEdocUrl ||
                    ''
                ).trim();

            if (isSigned && signedUrl) {
                // Persist signed URL separately, then expire session tokens.
                useEdocSessionStore.getState().setSignedEdocUrl(signedUrl);
                useEdocSessionStore.getState().clear();
                // Clear in-memory caches so we render Verify screen immediately.
                setIsVerified(false);
                queryClient.removeQueries({ queryKey: ['edocPublicMe'], exact: true });

                // Ensure we land on Verify screen and show modal with the signed URL.
                navigate(`/${encodeURIComponent(edocNumber)}`, {
                    replace: true,
                    state: { signedEdocUrlModal: signedUrl }
                });
                return;
            }

            showMessage('success', 'Order submitted successfully.', 6);
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'Failed to submit your order. Please try again.';
            showMessage('error', msg, 6);
        }
    };

    useEffect(() => {
        const successNotification = location.state?.successNotification;
        const successMessage = location.state?.successMessage;
        const signedEdocUrlModal = location.state?.signedEdocUrlModal;

        if (signedEdocUrlModal) {
            setSignedModalUrl(String(signedEdocUrlModal || '').trim());
            navigate(location.pathname, { replace: true, state: {} });
            return;
        }

        if (successNotification) {
            showNotification(
                successNotification.type || 'success',
                successNotification.message || 'Success',
                successNotification.description || '',
                null,
                successNotification.duration || 6,
                successNotification.placement || 'bottomRight'
            );
        } else if (successMessage) {
            showMessage('success', successMessage, 6);
        } else {
            return;
        }
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.pathname, location.state, navigate, showMessage, showNotification]);

    // If we already have a valid session (hard reload), /public/me will restore UI.
    if (isSessionReady) {
        const agreedAtText = new Date().toLocaleString();
        return (
            <EdocVerifiedView
                isDark={isDark}
                assignedUserLabel={assignedUserLabel}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                steps={steps}
                edocForm={edocForm}
                showCompany={showCompany}
                setShowCompany={setShowCompany}
                showAltOriginLine={showAltOriginLine}
                setShowAltOriginLine={setShowAltOriginLine}
                showAltDestinationLine={showAltDestinationLine}
                setShowAltDestinationLine={setShowAltDestinationLine}
                agreeTerms={agreeTerms}
                setAgreeTerms={setAgreeTerms}
                signatureName={signatureName}
                setSignatureName={setSignatureName}
                ipAddress={ipAddress}
                agreedAtText={agreedAtText}
                snapshot={snapshot}
                payment={payment}
                packages={packages}
                companyInfo={companyInfo}
                orderContactFallback={orderContactFallback}
                onBookOrder={handleBookOrder}
                booking={submitting}
            />
        );
    }

    const confirmIdentityCard = (
        <Container>
            <Modal
                open={Boolean(signedModalUrl)}
                title="Signed document"
                okText="Close"
                cancelButtonProps={{ style: { display: 'none' } }}
                onOk={() => {
                    setSignedModalUrl('');
                    useEdocSessionStore.getState().setSignedEdocUrl(null);
                    // Best-effort: browsers only allow closing windows opened by script.
                    window.close();
                }}
                onCancel={() => {
                    setSignedModalUrl('');
                    useEdocSessionStore.getState().setSignedEdocUrl(null);
                }}
            >
                <Typography.Paragraph style={{ marginBottom: 8 }}>
                    Your eDoc has been signed. Use the link below to view it.
                </Typography.Paragraph>
                <Typography.Text strong>Signed URL</Typography.Text>
                <div style={{ marginTop: 8 }}>
                    <a href={signedModalUrl} target="_blank" rel="noreferrer">
                        {signedModalUrl}
                    </a>
                </div>
            </Modal>
            <MobileLogoImage src={isDark ? logoDark : logoLight} alt="Dispatchly logo" />
            <AuthCard title="Confirm your identity">
                {hasDocIdentifier ? (
                    <AgentAssistBar>
                        <AgentAssistIdentity>
                            <AgentAssistAvatar>
                                <UserOutlined />
                            </AgentAssistAvatar>
                            <AgentAssistName title={`Assigned Agent: ${assignedUserLabel}`}>
                                Assigned Agent: {assignedUserLabel}
                            </AgentAssistName>
                        </AgentAssistIdentity>
                    </AgentAssistBar>
                ) : null}

                <IdentityHint>
                    {isEdocHome ? (
                        <>
                            {publicEdocQuery.isLoading ? (
                                <>
                                    Loading document details… <Spin size="small" style={{ marginLeft: 6 }} />
                                </>
                            ) : isEdocNotFound ? (
                                <>
                                    This link doesn’t match any document. Please request a fresh eDoc link from your agent.
                                </>
                            ) : isEdocFetchFailed ? (
                                <>
                                    We couldn’t load your document details. You can still try validating with your email or phone.
                                </>
                            ) : (
                                <>
                                    Enter <strong>either</strong> the email or phone we have on file for this document.
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            This link is missing a document identifier. Please open the eDoc link you received (it looks like <code style={{ fontSize: 12 }}>/ABC123…</code>).
                        </>
                    )}
                </IdentityHint>

                {!hasDocIdentifier ? (
                    <div
                        style={{
                            marginTop: 14,
                            padding: '14px 14px',
                            borderRadius: 12,
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E5E7EB'}`,
                            background: isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB'
                        }}
                    >
                        <Typography.Text
                            style={{
                                display: 'block',
                                fontSize: 14,
                                fontWeight: 650,
                                marginBottom: 8,
                                color: isDark ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.88)'
                            }}
                        >
                            What to do next
                        </Typography.Text>
                        <Typography.Text
                            style={{
                                display: 'block',
                                fontSize: 13,
                                lineHeight: 1.55,
                                color: isDark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.65)'
                            }}
                        >
                            1) Go back to the message/email from your agent.
                            <br />
                            2) Open the full eDoc link (it includes a code after the slash).
                            <br />
                            3) If the link looks incomplete, request a fresh one.
                        </Typography.Text>

                        <div style={{ marginTop: 12 }}>
                            <Typography.Text
                                style={{
                                    display: 'block',
                                    fontSize: 12,
                                    color: isDark ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.55)'
                                }}
                            >
                                Example format
                            </Typography.Text>
                            <div
                                style={{
                                    marginTop: 6,
                                    padding: '10px 12px',
                                    borderRadius: 10,
                                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB'}`,
                                    background: isDark ? 'rgba(0,0,0,0.22)' : '#FFFFFF',
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                    fontSize: 12,
                                    color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.78)',
                                    overflowX: 'auto'
                                }}
                            >
                                https://edocs-dispatchly.codefied.online/ABC123…
                            </div>
                        </div>
                    </div>
                ) : null}

                {canValidateForEdoc ? (
                    <Form form={form} onFinish={handleSubmit} layout="vertical" requiredMark={false}>
                        <Form.Item
                            name="email"
                            label={
                                <span>
                                    Email <Typography.Text type="secondary">(optional if phone is enough)</Typography.Text>
                                    {isEdocHome ? (
                                        <Typography.Text type="secondary" style={{ marginLeft: 6, fontWeight: 400 }}>
                                            — on file: {edocEmailHint || 'N/A'}
                                        </Typography.Text>
                                    ) : ENV.CLIENT_EMAIL ? (
                                        <Typography.Text type="secondary" style={{ marginLeft: 6, fontWeight: 400 }}>
                                            — on file: {maskEmail(ENV.CLIENT_EMAIL)}
                                        </Typography.Text>
                                    ) : null}
                                </span>
                            }
                            rules={[
                                {
                                    validator: async (_, value) => {
                                        const v = String(value || '').trim();
                                        if (!v) return;
                                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                        if (!emailRegex.test(v)) {
                                            throw new Error('Please enter a valid email address.');
                                        }
                                        if (!isEdocHome && ENV.CLIENT_EMAIL && v.toLowerCase() !== ENV.CLIENT_EMAIL.toLowerCase()) {
                                            throw new Error('This email is not authorized.');
                                        }
                                    }
                                }
                            ]}
                        >
                            <Input size="large" prefix={<UserOutlined />} placeholder="E.g. yourname@example.com" autoComplete="email" allowClear />
                        </Form.Item>

                        <Form.Item
                            name="phone"
                            label={
                                <span>
                                    Phone <Typography.Text type="secondary">(optional if email is enough)</Typography.Text>
                                    {isEdocHome ? (
                                        <Typography.Text type="secondary" style={{ marginLeft: 6, fontWeight: 400 }}>
                                            — on file: {edocPhoneHint || 'N/A'}
                                        </Typography.Text>
                                    ) : ENV.CLIENT_PHONE ? (
                                        <Typography.Text type="secondary" style={{ marginLeft: 6, fontWeight: 400 }}>
                                            — on file: {maskPhone(ENV.CLIENT_PHONE)}
                                        </Typography.Text>
                                    ) : null}
                                </span>
                            }
                            rules={[
                                {
                                    validator: async (_, value) => {
                                        const v = String(value || '').trim();
                                        if (!v) return;
                                        if (!/^[+()\d\s-]{7,20}$/.test(v)) {
                                            throw new Error('Please enter a valid phone number.');
                                        }
                                        if (!isEdocHome && ENV.CLIENT_PHONE && v !== ENV.CLIENT_PHONE) {
                                            throw new Error('This phone number is not authorized.');
                                        }
                                    }
                                }
                            ]}
                        >
                            <Input size="large" prefix={<PhoneOutlined />} placeholder="E.g. +1 (555) 123-4567" autoComplete="tel" allowClear />
                        </Form.Item>

                        <Form.Item>
                            <Button
                                size="large"
                                type="primary"
                                htmlType="submit"
                                block
                                loading={isEdocHome ? verifying : loading}
                            >
                                {submitButtonText}
                            </Button>
                        </Form.Item>
                    </Form>
                ) : null}
            </AuthCard>
        </Container>
    );

    // Hard reload: if token exists, /public/me is restoring session; avoid flashing verify screen.
    if (isEdocHome && meQuery?.isLoading) {
        return (
            <>
                <ScrollToTop />
                <OutsideContainer>
                    <OutsideLayoutContent>
                        <OutsideContent>
                            <IconWrapper>
                                <BannerPanel>
                                    <LogoImage src={logoDocs} alt="Dispatchly logo" />
                                    <BannerImage src={publicAsset('banner-img.png')} alt="Shipment performance preview" />
                                    <BannerHeadline>E-Docs Platform</BannerHeadline>
                                    <BannerSubheadline>
                                        Simplify customer e-signatures and consent management in one secure platform..
                                    </BannerSubheadline>
                                </BannerPanel>
                            </IconWrapper>
                            <FormWrapper>
                                <FormInner>
                                    <div style={{ width: '100%', display: 'grid', placeItems: 'center', padding: 24 }}>
                                        <Spin />
                                        <Typography.Text type="secondary" style={{ marginTop: 10 }}>
                                            Restoring your session…
                                        </Typography.Text>
                                    </div>
                                </FormInner>
                            </FormWrapper>
                        </OutsideContent>
                    </OutsideLayoutContent>
                </OutsideContainer>
            </>
        );
    }

    // Public eDoc route should own its layout so we can remove the banner after validation.
    if (isEdocHome) {
        return (
            <>
                <ScrollToTop />
                <OutsideContainer>
                    <OutsideLayoutContent>
                        <OutsideContent>
                            <IconWrapper>
                                <BannerPanel>
                                    <LogoImage src={logoDocs} alt="Dispatchly logo" />
                                    <BannerImage
                                        src={publicAsset('banner-img.png')}
                                        alt="Shipment performance preview"
                                    />
                                    <BannerHeadline>E-Docs Platform</BannerHeadline>
                                    <BannerSubheadline>
                                        Simplify customer e-signatures and consent management in one secure platform..
                                    </BannerSubheadline>
                                </BannerPanel>
                            </IconWrapper>
                            <FormWrapper>
                                <FormInner>{confirmIdentityCard}</FormInner>
                            </FormWrapper>
                        </OutsideContent>
                    </OutsideLayoutContent>
                </OutsideContainer>
            </>
        );
    }

    return confirmIdentityCard;
};

export default Signin;
